import { formatClientDateTime } from '@/lib/client-locale'
export function formatRelativeTimeIso(
  iso: string | null | undefined,
  nowMs = Date.now()
): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000))
  if (sec < 10) return 'just now'
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 48) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  return `${d}d ago`
}

/** Whole calendar days elapsed since an ISO timestamp (floor). */
export function calendarDaysSinceIso(iso: string, nowMs = Date.now()): number {
  const timestamp = new Date(iso).getTime()
  if (Number.isNaN(timestamp)) return 0
  return Math.max(0, Math.floor((nowMs - timestamp) / (24 * 60 * 60 * 1000)))
}

/** Elapsed wall time since start for "on call" display, e.g. "4m 02s" */
export function formatElapsedSinceIso(
  iso: string | null | undefined,
  nowMs = Date.now()
): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000))
  const m = Math.floor(sec / 60)
  const s = sec % 60
  if (m === 0) return `${s}s`
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

/**
 * Local wall-clock time when the call started (not elapsed).
 * Same calendar day as `nowMs`: time only; otherwise short date + time.
 */
export function formatStartedAtLocalClock(
  iso: string | null | undefined,
  nowMs = Date.now()
): string | null {
  if (!iso) return null
  const started = new Date(iso)
  if (Number.isNaN(started.getTime())) return null
  const now = new Date(nowMs)
  const sameCalendarDay =
    started.getFullYear() === now.getFullYear() &&
    started.getMonth() === now.getMonth() &&
    started.getDate() === now.getDate()
  if (sameCalendarDay) {
    return formatClientDateTime(started, {
      hour: 'numeric',
      minute: '2-digit',
    })
  }
  return formatClientDateTime(started, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Clock style duration m:ss */
export function formatDurationClock(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Analysis session timestamp with local date, time, and timezone abbreviation. */
export function formatAnalysisLastUpdated(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return formatClientDateTime(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}
